import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import {
    getOperateType,
    HOTKEY_TYPE,
    useFramework,
    useIdentity,
    useMoveEventListener,
    useScale,
    useStagePadding,
    useStageScrollPosition,
} from '@/framework';
import { getTargetType } from '@/framework/stage/utils/node-kit';
import {
    BaseLayerViewType,
    REFERENCE_LINE_DIRECTION,
    REFERENCE_LINE_EVENT_NAMES,
    RULER_BAR_SIZE,
    type ReferenceLine,
} from '@/version-types';
import { useAddReferenceLineFromRuler } from '../hooks/use-add-reference-line-from-ruler';
import { useReferenceLinesEvents } from '../hooks/use-reference-lines-events';
import { DeleteReferenceLine } from '../operations/delete-reference-line';
import { ShowReferenceLines } from '../operations/show-reference-lines';
import { UpdateReferenceLine } from '../operations/update-reference-line';
import { useReferenceLinesState } from '../states/reference-lines-state';
import { getContainerRect } from '../utils';
import PositionTip from './position-tip';
import './index.less';

const ReferenceLineLayer: React.FC = () => {
    const [selectedLineId, setSelectedLineId] = useState('');
    const [highlightReferenceLineIds, setHighlightReferenceLineIds] = useState<string[]>([]);
    const [oldReferenceLine, setOldReferenceLine] = useState<ReferenceLine>();
    const [isHoveringOverOperator, setIsHoveringOverOperator] = useState(false);

    // 记录是否有其他操作, 有其他操作时, 参考线需要禁用 hover 态
    const [isOtherOperation, setIsOtherOperation] = useState(false);

    const identity = useIdentity();
    const framework = useFramework();

    const { paddingLeft, paddingTop } = useStagePadding();

    // 监听画布尺寸更新, 对应调整参考线的位置
    useReferenceLinesEvents();
    // 从标尺处拖动添加参考线
    useAddReferenceLineFromRuler(paddingLeft, paddingTop);

    // 参考线状态
    const referenceLinesState = useReferenceLinesState();
    const showReferenceLines = referenceLinesState((state) => state.showReferenceLines);
    const referenceLines = referenceLinesState((state) => state.referenceLines);
    const lockReferenceLines = referenceLinesState((state) => state.lockReferenceLines);
    const draggingLineId = referenceLinesState((state) => state.draggingLineId);
    const setDraggingLineId = referenceLinesState((state) => state.setDraggingLineId);
    const setTooltipPosition = referenceLinesState((state) => state.setTooltipPosition);
    const setDraggingLine = referenceLinesState((state) => state.setDraggingLine);
    const setContainerRect = referenceLinesState((state) => state.setContainerRect);
    const setRulerBarSize = referenceLinesState((state) => state.setRulerBarSize);

    useEffect(() => {
        // 同步参考线
        framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC, referenceLines);
        // 激活图层初始化比参考线晚, 所以需要主动发起参考线同步请求
        const handler = framework.events.on(REFERENCE_LINE_EVENT_NAMES.REQUEST_SYNC, () => {
            framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC, referenceLines);
        });
        return () => framework.events.off(handler);
    }, [referenceLines]);

    useEffect(() => {
        let eventId: number = 0;
        if (showReferenceLines) {
            eventId = framework.events.on(REFERENCE_LINE_EVENT_NAMES.HIGHLIGHT, setHighlightReferenceLineIds);
        }

        return () => {
            if (eventId) {
                framework.events.off(eventId);
            }
        };
    }, [showReferenceLines]);

    const updateAction = new UpdateReferenceLine(identity);
    const showAction = new ShowReferenceLines(identity);
    const delAction = new DeleteReferenceLine(identity);

    const scale = useScale();

    const stageScrollPosition = useStageScrollPosition();

    // 拖拽参考线的时候, 不要禁用
    const disableEvents = !draggingLineId && (isHoveringOverOperator || lockReferenceLines || !showReferenceLines);

    // 监听鼠标是否有移动到操作手柄上
    useEffect(() => {
        const onMouseMove = (e) => {
            const operateType = getOperateType(e.target);
            if (operateType && !isHoveringOverOperator) {
                setIsHoveringOverOperator(true);
            } else if (!operateType && isHoveringOverOperator) {
                setIsHoveringOverOperator(false);
            }
        };
        document.addEventListener('mousemove', onMouseMove);

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
        };
    }, [isHoveringOverOperator]);

    // 最小删除位置
    const minPosition = useRef(Infinity);
    const maxPosition = useRef(-Infinity);

    // 拖动事件
    useMoveEventListener((e) => {
        if (disableEvents) return;

        const operationType = getTargetType(e.target);

        if (!operationType) return;

        // 操作的是参考线
        if (operationType === BaseLayerViewType.REFERENCE_LINE_MOVE) {
            const lineId = e.target.getAttribute('data-line-id');

            if (!lineId) return;

            const line = referenceLines.find((line) => line.id === lineId);
            const isVertical = line?.direction === REFERENCE_LINE_DIRECTION.VERTICAL;
            if (!line) return;

            if (e.isStart) {
                setDraggingLineId(lineId);
                setDraggingLine(line);
                setOldReferenceLine({ ...line });

                // 选中参考线, 就要取消图层的选中
                framework.operate.delActiveLayer();

                // 禁用编辑器的其他操作
                framework.operate.disablePick();

                const rect = getContainerRect();
                if (rect) {
                    setContainerRect(rect);
                    const hasRulerBar = !!document.querySelector('.l-ruler-bar__canvas');
                    const rulerBarSize = hasRulerBar ? RULER_BAR_SIZE : 0;
                    setRulerBarSize(rulerBarSize);

                    minPosition.current = isVertical ? rect.left + rulerBarSize : rect.top + rulerBarSize;

                    maxPosition.current = isVertical ? rect.right : rect.bottom;
                }

                return;
            }

            setTooltipPosition({
                x: e.currentPoint.x,
                y: e.currentPoint.y,
            });

            const deltaPosition = isVertical ? e.deltaX : e.deltaY;

            const position = line.position + deltaPosition / scale;

            // 拖拽结束
            if (e.isEnd) {
                setDraggingLineId('');
                setSelectedLineId('');
                setDraggingLine(null);
                // 恢复编辑器的其他操作
                framework.operate.enablePick();

                const outOfRange = isVertical
                    ? e.currentPoint.x < minPosition.current || e.currentPoint.x > maxPosition.current
                    : e.currentPoint.y < minPosition.current || e.currentPoint.y > maxPosition.current;

                if (outOfRange) {
                    if (oldReferenceLine) {
                        updateAction.update({ ...oldReferenceLine }, undefined, false);
                    }
                    delAction.delete(lineId);
                } else {
                    // 更新参考线位置(写入历史记录)
                    updateAction.update(
                        {
                            ...line,
                            position,
                        },
                        oldReferenceLine,
                    );
                }
            } else {
                const newLine = {
                    ...line,
                    position,
                };
                // 临时更新参考线位置(不写入历史记录)
                updateAction.update(newLine, undefined, false);

                setDraggingLine(newLine);
            }
        }
        // 其他操作, 只需禁用参考线 hover 态即可
        else {
            if (e.isStart) {
                setIsOtherOperation(true);
            }
            if (e.isEnd) {
                setIsOtherOperation(false);
            }
        }
    });

    // 键盘事件(删除参考线)
    useEffect(() => {
        const handler = framework.events.onDelete(() => {
            if (selectedLineId) {
                delAction.delete(selectedLineId);
                setSelectedLineId('');
            }
        });
        return () => framework.events.off(handler);
    }, [selectedLineId]);

    // 键盘事件(显示/隐藏参考线)
    useEffect(() => {
        const handler = framework.events.on(HOTKEY_TYPE.toggleReferenceLine, () => {
            showAction.show(!showReferenceLines);
        });
        return () => framework.events.off(handler);
    }, [showReferenceLines]);

    // 点击事件
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (framework.operate.isDisableOperate()) return;

            const operationType = getTargetType(event.target);

            const target = event.target as HTMLElement;

            if (!operationType || operationType !== BaseLayerViewType.REFERENCE_LINE_MOVE) {
                setSelectedLineId('');
            }

            const lineId = target.getAttribute('data-line-id');
            setSelectedLineId(lineId || '');

            // 选中参考线, 就要取消图层的选中
            if (lineId) {
                framework.operate.delActiveLayer();
            }
        }

        // 形状笔跟参考线会冲突, 用形状笔的时候禁止参考线的点击事件
        if (!disableEvents) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [disableEvents]);

    if (!showReferenceLines) return null;

    return (
        <div className="l-reference-lines">
            {referenceLines.map(({ position, direction, id: lineId }) => {
                const style =
                    direction === REFERENCE_LINE_DIRECTION.VERTICAL
                        ? { left: position * scale + paddingLeft - stageScrollPosition.left }
                        : { top: position * scale + paddingTop - stageScrollPosition.top };

                const isHorizontal = direction === REFERENCE_LINE_DIRECTION.HORIZONTAL;
                const isVertical = direction === REFERENCE_LINE_DIRECTION.VERTICAL;
                const isDragging = draggingLineId === lineId;
                const isHighlight = highlightReferenceLineIds.includes(lineId);
                const isSelected = selectedLineId === lineId;

                return (
                    <div
                        key={lineId}
                        data-type={BaseLayerViewType.REFERENCE_LINE_MOVE}
                        data-line-id={lineId}
                        className={classnames('l-reference-line', {
                            ['l-reference-line__vertical']: isVertical,
                            ['l-reference-line__horizontal']: isHorizontal,
                            ['l-reference-line__dragging']: isDragging,
                            ['l-reference-line__no-events']: isOtherOperation || disableEvents,
                            ['l-reference-line__highlight']: isHighlight,
                            ['l-reference-line__selected']: isSelected,
                        })}
                        style={style}
                    ></div>
                );
            })}

            {/* 参考线坐标的 tooltip */}
            <PositionTip />
        </div>
    );
};

export default ReferenceLineLayer;
